import { useImperativeHandle, Ref, useMemo } from 'react';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import PageSizeSelector from '@/components/PageSizeSelector/PageSizeSelector';
import styles from '@/components/filters/FiltersArea.module.scss';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import { getClassificationStatusColors } from '@/helpers/cssHelpers';

export interface ClassificationStatusGroup {
  id: string;
  statuses: ContactClassificationStatus[];
  iconColor: string;
}

export const getClassificationStatusGroups = (): ClassificationStatusGroup[] => {
  const colors = getClassificationStatusColors();
  return [
    { id: ContactClassificationStatus.PendingApproval, statuses: [ContactClassificationStatus.PendingApproval], iconColor: colors[ContactClassificationStatus.PendingApproval] },
    { id: ContactClassificationStatus.PendingCompletion, statuses: [ContactClassificationStatus.PendingCompletion], iconColor: colors[ContactClassificationStatus.PendingCompletion] },
    { id: ContactClassificationStatus.CompletionReturned, statuses: [ContactClassificationStatus.CompletionReturned], iconColor: colors[ContactClassificationStatus.CompletionReturned] },
    { id: ContactClassificationStatus.Approved, statuses: [ContactClassificationStatus.Approved], iconColor: colors[ContactClassificationStatus.Approved] },
    { id: ContactClassificationStatus.Rejected, statuses: [ContactClassificationStatus.Rejected], iconColor: colors[ContactClassificationStatus.Rejected] },
    { id: ContactClassificationStatus.Cancelled, statuses: [ContactClassificationStatus.Cancelled], iconColor: colors[ContactClassificationStatus.Cancelled] },
  ];
};

const HIGHLIGHTED_STATUSES = new Set([ContactClassificationStatus.CompletionReturned]);

interface FiltersAreaProps {
  selectedFilters: string[];
  onFiltersChange: (filters: string[]) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  statusCounts?: Record<string, number>;
  ref?: Ref<{ resetFilters: () => void }>;
}

const FiltersArea = ({
  selectedFilters,
  onFiltersChange,
  pageSize,
  onPageSizeChange,
  statusCounts,
  ref,
}: FiltersAreaProps) => {
  // Memoize status groups with CSS variable colors
  const classificationStatusGroups = useMemo(() => getClassificationStatusGroups(), []);

  const resetAllFilters = () => {
    onFiltersChange([]);
  };

  const handleStatusGroupChange = (groupId: string) => {
    const isSelected = selectedFilters.includes(groupId);

    if (isSelected) {
      onFiltersChange(selectedFilters.filter((f) => f !== groupId));
    } else {
      onFiltersChange([...selectedFilters, groupId]);
    }
  };

  useImperativeHandle(ref, () => ({
    resetFilters: resetAllFilters,
  }));

  return (
    <div className={styles.filtersArea}>
      <div className={styles.filtersContainer}>
        {classificationStatusGroups.map((statusGroup) => {
          const hasItems = statusGroup.statuses.some(s => (statusCounts?.[s] ?? 0) > 0);
          const isHighlighted = hasItems && HIGHLIGHTED_STATUSES.has(statusGroup.statuses[0]);
          return (
            <StatusFilter
              key={statusGroup.id}
              title={statusGroup.id}
              isSelected={selectedFilters.includes(statusGroup.id)}
              onClick={() => handleStatusGroupChange(statusGroup.id)}
              iconColor={statusGroup.iconColor}
              isHighlighted={isHighlighted}
            />
          );
        })}
      </div>
      <div className={styles.controlsContainer}>
        <PageSizeSelector value={pageSize} onChange={onPageSizeChange} />
      </div>
    </div>
  );
};

export default FiltersArea;

